import React, { useState, useEffect } from 'react';
// import { motion, AnimatePresence } from 'framer-motion';
// import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend } from 'chart.js';

// Register ChartJS components
ChartJS.register(ArcElement, ChartTooltip, Legend);

const EmiCalculatorPage = () => {
  return (
    <div className="calculator-container">
      
      
      <div className="calculator-content">
        <EMICalculator />
      </div>
      
      <style jsx>{`
        .calculator-container {
          max-width: 1000px;
          margin: 5.5rem auto ;
          padding: 0 1rem;
          min-height: calc(100vh - 64px);
          display: flex;
          flex-direction: column;
        }
        
        .calculator-header {
          text-align: center;
          margin-bottom: 1.5rem;
          padding: 0 0.5rem 1rem;
        }
        
        .calculator-header h1 {
          font-size: 2rem;
          font-weight: 700;
          color: #1a202c;
          margin: 0 0 0.5rem 0;
          line-height: 1.2;
        }
        
        .calculator-header p {
          color: #4a5568;
          font-size: 1rem;
          margin: 0;
          opacity: 0.9;
        }
        
        .calculator-content {
          background: white;
          border-radius: 1rem;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.03);
          padding: 1.5rem;
          flex: 1;
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        
        @media (max-width: 768px) {
          .calculator-container {
            padding: 1.5rem 1rem;
            min-height: auto;
          }
          
          .calculator-header h1 {
            font-size: 1.75rem;
          }
          
          .calculator-content {
            padding: 3rem;
          }
        }
      `}</style>
    </div>
  );
};

export default EmiCalculatorPage;

const EMICalculator = () => {
  const [loanAmount, setLoanAmount] = useState(3000000);
  const [interestRate, setInterestRate] = useState(12);
  const [tenure, setTenure] = useState(60);
  const [emi, setEmi] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalPayment, setTotalPayment] = useState(0);
  const calculateEMI = (loanAmount, interestRate, tenure) => {
  if (!loanAmount || !interestRate || !tenure) {
    return { emi: 0, totalInterest: 0, totalPayment: 0 };
  }

  const monthlyRate = interestRate / 12 / 100;
  const emi =
    (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, tenure)) /
    (Math.pow(1 + monthlyRate, tenure) - 1);

  const totalPayment = emi * tenure;

  return {
    emi,
    totalPayment,
    totalInterest: totalPayment - loanAmount,
  };
};

useEffect(() => {
  const result = calculateEMI(loanAmount, interestRate, tenure);
  setEmi(result.emi);
  setTotalPayment(result.totalPayment);
  setTotalInterest(result.totalInterest);
}, [loanAmount, interestRate, tenure]);


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // const formatPercentage = (value) => {
  //   return `${value}%`;
  // };
  
  // const formatMonths = (months) => {
  //   const years = Math.floor(months / 12);
  //   const remainingMonths = months % 12;
  //   if (years === 0) return `${months} months`;
  //   if (remainingMonths === 0) return `${years} year${years > 1 ? 's' : ''}`;
  //   return `${years} year${years > 1 ? 's' : ''} ${remainingMonths} months`;
  // };

  // Chart data
const chartData = React.useMemo(() => ({
  labels: ['Principal', 'Interest'],
  datasets: [
    {
      data: [loanAmount, totalInterest],
      backgroundColor: ['#48bb78', '#2d3748'],
      borderWidth: 0,
    },
  ],
}), [loanAmount, totalInterest]);

const chartOptions = React.useMemo(() => ({
  responsive: true,
  maintainAspectRatio: false,
  cutout: '70%',
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        boxWidth: 10,
        padding: 20,
        usePointStyle: true,
      },
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const value = context.raw || 0;
          const total = context.dataset.data.reduce((a, b) => a + b, 0);
          const percentage = Math.round((value / total) * 100);
          return `${context.label}: ${formatCurrency(value)} (${percentage}%)`;
        },
      },
    },
  },
}), []); // ✅ empty dependency array


// const handleAmountChange = (e, setter) => {
//   const rawValue = e.target.value.replace(/\D/g, '');
//   setter(Number(rawValue) || 0);
// };
  return (
    <div className="calculator">
      <div className="calculator-grid">
        {/* Chart Section */}
        <div className="chart-container">
          <div className="chart-wrapper">
            <Doughnut data={chartData} options={chartOptions} />
            <div className="chart-center-text">
              <div className="chart-amount">{formatCurrency(emi)}</div>
              <div className="chart-label">Monthly EMI</div>
            </div>
          </div>
          
          {/* Summary Section */}
          <div className="summary">
            <div className="summary-item">
              <span>Loan Amount</span>
              <span className="summary-value">{formatCurrency(loanAmount)}</span>
            </div>
            <div className="summary-item">
              <span>Total Interest</span>
              <span className="summary-value">{formatCurrency(totalInterest)}</span>
            </div>
            <div className="summary-item total">
              <span>Total Payment</span>
              <span className="summary-value">{formatCurrency(totalPayment)}</span>
            </div>
          </div>
        </div>
        
        {/* Inputs Section */}
        <div className="inputs-container">
          {/* EMI Display */}
          <div className="emi-display">
            <div className="emi-amount">
              {formatCurrency(emi)}<span className="per-month">/mo</span>
            </div>
            <div className="emi-label">Monthly EMI</div>
          </div>
          
          {/* Input Controls */}
          <div className="input-group">
            <label>
              Loan Amount
              <small> (1L – 1Cr)</small>
            </label>
            <div className="input-wrapper">
              <input
                type="range"
                min="100000"
                max="10000000"
                step="10000"
                value={loanAmount}
                onChange={(e) => setLoanAmount(Number(e.target.value))}
                className="slider"
              />
              <div className="input-field">
                <input
                  type="text"
                  value={loanAmount.toLocaleString('en-IN')}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setLoanAmount(Number(value) || 0);
                  }}
                  className="amount-input"
                />
                <span className="input-suffix">₹</span>
              </div>
            </div>
          </div>
          
          <div className="input-group">
            <label>Interest Rate (p.a.)</label>
            <div className="input-wrapper">
              <input
                type="range"
                min="1"
                max="30"
                step="0.1"
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                className="slider"
              />
              <div className="input-field">
                <input
                  type="number"
                  min="1"
                  max="30"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => setInterestRate(Math.min(30, Math.max(1, Number(e.target.value) || 1)))}
                  className="rate-input"
                />
                <span className="input-suffix">%</span>
              </div>
            </div>
          </div>
          
          <div className="input-group">
            <label>Loan Tenure</label>
            <div className="input-wrapper">
              <input
                type="range"
                min="6"
                max="84"
                step="1"
                value={tenure}
                onChange={(e) => setTenure(Number(e.target.value))}
                className="slider"
              />
              <div className="input-field">
                <input
                  type="number"
                  min="6"
                  max="84"
                  value={tenure}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTenure(value === '' ? '' : Number(value));
                  }}
                  onBlur={() => setTenure((value) => Math.min(84, Math.max(6, Number(value) || 6)))}
                  className="tenure-input"
                />
                <span className="input-suffix">months</span>
              </div>
            </div>
          </div>
        </div>
        
        <style jsx>{`
          .calculator {
            display: flex;
            flex-direction: column;
            height: 100%;
            gap: 1.5rem;
          }
          
          .calculator-grid {
            display: grid;
            grid-template-columns: 1.2fr 1fr;
            gap: 2rem;
            height: 100%;
            min-height: 0;
          }
          
          .chart-container, .inputs-container {
            display: flex;
            flex-direction: column;
            height: 100%;
          }
          
          .chart-container {
            justify-content: center;
          }
          
          .inputs-container {
            overflow-y: auto;
            padding-right: 0.5rem;
          }
          
          .chart-container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: 2.5rem;
            padding: 2rem 1rem;
            height: 100%;
          }
          
          .chart-wrapper {
            position: relative;
            width: 100%;
            max-width: 320px;
            height: 320px;
            margin: 0 auto 1.5rem;
          }
          
          .chart-center-text {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            text-align: center;
            width: 100%;
            padding: 1rem;
          }
          
          .chart-amount {
            font-size: 1.75rem;
            font-weight: 700;
            color: #2d3748;
            line-height: 1.2;
            white-space: nowrap;
            margin-bottom: 0.25rem;
          }
          
          .chart-label {
            font-size: 1rem;
            color: #64748b;
            margin: 0;
            font-weight: 500;
            opacity: 0.9;
          }
          
          .emi-display {
            text-align: center;
            margin-bottom: 1rem;
          }
          
          .emi-amount {
            font-size: 1.5rem;
            font-weight: 700;
            color: #2d3748;
            line-height: 1.3;
          }
          
          .emi-amount .per-month {
            font-size: 0.9rem;
            color: #64748b;
            margin-left: 0.25rem;
          }
          
          .emi-label {
            color: #64748b;
            font-size: 0.9rem;
            margin-top: 0.25rem;
            font-weight: 500;
          }
          
          .input-group {
            margin-bottom: 1.25rem;
          }
          
          .input-group:last-child {
            margin-bottom: 0;
          }
          
          .input-group label {
            display: block;
            margin-bottom: 0.6rem;
            font-size: 0.9375rem;
            font-weight: 500;
            color: #334155;
          }
          
          .input-group label small {
            font-size: 0.8em;
            color: #94a3b8;
            margin-left: 0.25rem;
            font-weight: 400;
          }
          
          .input-wrapper {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
          }
          
          .slider {
            width: 100%;
            height: 6px;
            -webkit-appearance: none;
            background: #e2e8f0;
            border-radius: 3px;
            outline: none;
            margin: 0.5rem 0;
          }
          
          .slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            width: 18px;
            height: 18px;
            background: #66BF01;
            border-radius: 50%;
            cursor: pointer;
            box-shadow: 0 2px 4px rgba(37, 99, 235, 0.3);
            transition: all 0.2s ease;
          }
          
          .slider::-webkit-slider-thumb:hover {
            transform: scale(1.1);
            box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.2);
          }
          
          .input-field {
            position: relative;
            display: flex;
            align-items: center;
            width: 100%;
          }
          
          .input-field input[type="number"],
          .input-field input[type="text"] {
            width: 100%;
            padding: 0.7rem 1rem;
            border: 1px solid #e2e8f0;
            border-radius: 0.5rem;
            font-size: 0.9375rem;
            color: #1e293b;
            background: #fff;
            transition: all 0.2s ease;
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
          }
          
          .input-field input[type="number"]:focus,
          .input-field input[type="text"]:focus {
            outline: none;
            border-color: #a5b4fc;
            box-shadow: 0 0 0 3px rgba(199, 210, 254, 0.5);
          }
          
          .input-prefix, 
          .input-suffix {
            position: absolute;
            font-size: 0.9375rem;
            color: #64748b;
            pointer-events: none;
          }
          
          .input-prefix {
            left: 1rem;
          }
          
          .input-suffix {
            right: 1rem;
          }
          
          /* Adjust input padding when there's a prefix/suffix */
          .input-prefix + input {
            padding-left: 2.5rem;
          }
          
          .input-suffix + input {
            padding-right: 2.5rem;
          }
          
          .summary {
            margin-top: 1.5rem;
            border-top: 1px solid #e2e8f0;
            padding: 1.5rem 0;
            margin-bottom: 1rem;
          }
          
          .summary-item {
            display: flex;
            justify-content: space-between;
            margin-bottom: 0.75rem;
            color: #4a5568;
          }
          
          .summary-item.total {
            border-top: 1px solid #e2e8f0;
            font-weight: 600;
            color: #2d3748;
          }
          
          @media (max-width: 768px) {
            .calculator-grid {
              grid-template-columns: 1fr;
              gap: 2.5rem;
            }
            
            .chart-container {
              gap: 2rem;
              padding: 0;
            }
            
            .chart-wrapper {
              max-width: 280px;
              height: 280px;
              margin: 0 auto 1.5rem;
            }
            
            .inputs-container {
              padding: 0;
            }
            
            .summary {
              margin-top: 0.5rem;
              font-size: 0.8rem;
            }
            
            .input-wrapper {
              flex-direction: column;
              align-items: flex-start;
              gap: 0.5rem;
            }
            
            input[type="range"] {
              width: 100%;
            }
            
            .amount-input,
            .rate-input,
            .tenure-input {
              width: 100%;
            }
            
            .summary-item,
            .summary-total {
              font-size: 0.9rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};


/* ================= CREATIVE STYLES (Light Theme + Your Colors) ================= */
// const styles = `
// :root {
//   --green: #7ED321;
//   --blue: #2C3E50;
//   --text-dark: #0f172a;
//   --light-bg: #fafbff;
//   --card-bg: #ffffff;
//   --border: #e2e8f0;
// }

// .emi-page {
//   background: var(--light-bg);
//   color: var(--text-dark);
//   padding: 80px 6vw 100px;
//   font-family: 'Inter', sans-serif;
// }

// .emi-container {
//   max-width: 960px;
//   margin: 0 auto;
// }

// .emi-header {
//   text-align: center;
//   margin-bottom: 60px;
// }

// .emi-header .eyebrow {
//   color: var(--green);
//   font-size: 14px;
//   font-weight: 600;
//   letter-spacing: 0.05em;
//   display: block;
//   margin-bottom: 12px;
// }

// .emi-header h1 {
//   font-size: 42px;
//   font-weight: 800;
//   color: var(--text-dark);
//   margin-bottom: 16px;
// }

// .emi-header p {
//   color: #64748b;
//   font-size: 18px;
//   max-width: 600px;
//   margin: 0 auto;
// }

// /* Creative Layout */
// .emi-creative {
//   display: flex;
//   flex-direction: column;
//   gap: 48px;
// }

// /* Summary Orb */
// .summary-orb {
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   gap: 24px;
//   background: var(--card-bg);
//   border: 1px solid var(--border);
//   border-radius: 24px;
//   padding: 32px;
//   box-shadow: 0 6px 20px rgba(44, 62, 80, 0.05);
// }

// .orb-labels {
//   text-align: left;
// }

// .orb-value {
//   font-size: 28px;
//   font-weight: 800;
//   color: var(--text-dark);
//   display: block;
// }

// .orb-desc {
//   font-size: 15px;
//   color: #64748b;
//   margin-top: 4px;
// }

// /* Timeline Visualization */
// .timeline-visualization {
//   background: var(--card-bg);
//   border: 1px solid var(--border);
//   border-radius: 20px;
//   padding: 28px;
//   box-shadow: 0 6px 20px rgba(44, 62, 80, 0.05);
// }

// .timeline-bar {
//   height: 24px;
//   border-radius: 12px;
//   overflow: hidden;
//   background: #f1f5f9;
//   display: flex;
// }

// .timeline-principal {
//   background: var(--green);
//   height: 100%;
//   transition: width 0.4s ease;
// }

// .timeline-interest {
//   background: var(--blue);
//   height: 100%;
//   transition: width 0.4s ease;
// }

// .timeline-legend {
//   display: flex;
//   gap: 24px;
//   margin-top: 16px;
//   font-size: 14px;
//   color: #64748b;
// }

// .dot {
//   display: inline-block;
//   width: 10px;
//   height: 10px;
//   border-radius: 50%;
//   margin-right: 6px;
// }

// .dot.principal { background: var(--green); }
// .dot.interest { background: var(--blue); }

// /* Adjusters */
// .adjuster-grid {
//   display: grid;
//   grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
//   gap: 24px;
// }

// .adjuster {
//   background: var(--card-bg);
//   border: 1px solid var(--border);
//   border-radius: 18px;
//   padding: 24px;
//   box-shadow: 0 4px 14px rgba(44, 62, 80, 0.04);
//   transition: all 0.3s ease;
//   position: relative;
//   overflow: hidden;
// }

// .adjuster::after {
//   content: '';
//   position: absolute;
//   bottom: 0;
//   left: 0;
// }

// .adjuster-header label {
//   font-weight: 600;
//   color: var(--text-dark);
// }

// .adjuster-value {
//   font-weight: 600;
//   color: var(--green);
// }

// .adjuster-slider {
//   width: 100%;
//   height: 6px;
//   -webkit-appearance: none;
//   background: #e2e8f0;
//   border-radius: 3px;
//   outline: none;
// }

// .adjuster-slider::-webkit-slider-thumb {
//   -webkit-appearance: none;
//   width: 20px;
//   height: 20px;
//   border-radius: 50%;
//   background: var(--green);
//   border: 2px solid white;
//   box-shadow: 0 2px 6px rgba(0,0,0,0.15);
//   cursor: pointer;
// }

// /* Final Totals */
// .final-totals {
//   display: flex;
//   justify-content: center;
//   gap: 40px;
// }

// .total-item {
//   text-align: center;
// }

// .total-item span {
//   display: block;
//   font-size: 15px;
//   color: #64748b;
//   margin-bottom: 8px;
// }

// .total-item strong {
//   font-size: 22px;
//   font-weight: 700;
//   color: var(--text-dark);
// }

// /* Responsive */
// @media (max-width: 768px) {
//   .summary-orb {
//     flex-direction: column;
//     text-align: center;
//   }

//   .final-totals {
//     flex-direction: column;
//     gap: 16px;
//   }

//   .emi-header h1 {
//     font-size: 34px;
//   }
// }

// @media (max-width: 480px) {
//   .emi-page {
//     padding: 70px 5vw 90px;
//   }

//   .adjuster-grid {
//     grid-template-columns: 1fr;
//   }

//   .orb-value {
//     font-size: 24px;
//   }
// }
// `;
